import React from 'react';
import { trackStrengthsCTAClick } from '../../mixpanel/events';

const FounderStrengthsSection = () => {
  const handleCTAClick = () => {
    trackStrengthsCTAClick();
  };

  return (
    <section className="bg-apple-gray-100 text-apple-gray-800 py-20 px-4" id="founder-strengths">
      <div className="max-w-4xl mx-auto text-center">
        {/* Main Headline */}
        <h2 className="text-3xl md:text-4xl font-bold text-apple-gray-800 mb-6 leading-tight">
          You Already Have What It Takes to Succeed
        </h2>

        {/* Subheadline */}
        <h3 className="text-xl md:text-2xl font-semibold text-apple-gray-600 mb-8">
          As a founder, you bring invaluable strengths to the table
        </h3>

        {/* Strength Points */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center mb-3">
                <div className="bg-apple-gray-800 p-2 rounded-lg mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-apple-gray-800">Clear Vision</h4>
              </div>
              <p className="text-apple-gray-600">You know exactly what problem you're solving and how your product should work for your customers.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center mb-3">
                <div className="bg-apple-gray-800 p-2 rounded-lg mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h4 className="font-semibold text-apple-gray-800">Understanding of the Business</h4>
              </div>
              <p className="text-apple-gray-600">You understand your market, revenue model, and what features will actually drive business value.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center mb-3">
                <div className="bg-apple-gray-800 p-2 rounded-lg mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-apple-gray-800">Market Knowledge</h4>
              </div>
              <p className="text-apple-gray-600">You know your customers' pain points, competitive landscape, and what success looks like in your industry.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center mb-3">
                <div className="bg-apple-gray-800 p-2 rounded-lg mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-apple-gray-800">User Insight</h4>
              </div>
              <p className="text-apple-gray-600">You have direct access to your users and understand their needs better than any developer ever could.</p>
            </div>
          </div>
        </div>

        {/* Full-width emphasis box */}
        <div className="bg-white p-8 rounded-lg shadow-sm mb-12 max-w-3xl mx-auto">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-apple-gray-800 p-3 rounded-lg mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h4 className="text-xl font-bold text-apple-gray-800">The Missing Piece</h4>
          </div>
          <p className="text-lg text-apple-gray-600 leading-relaxed">
            What you're missing is a deep understanding of how the tech world really works. It's a wild west out there, and without the right guidance, even the smartest founders get burned.
          </p>
        </div>

        {/* Secondary Title */}
        <div className="mb-12">
          <h3 className="text-2xl md:text-3xl font-bold text-apple-gray-800 mb-4">
            You’ve Done the Hard Part. The Tech Doesn’t Have to Hurt.
          </h3>
          <p className="text-lg text-apple-gray-600 max-w-2xl mx-auto">
            You don't need to give away equity to a CTO. What you need is an experienced technical advisor who understands the tech industry, someone who can protect you from making 7 figure mistakes, and guide you without diluting your ownership.
          </p>
        </div>

        {/* Call to Action */}
        <div className="bg-apple-gray-800 text-white p-8 rounded-lg">
          <h4 className="text-xl font-semibold mb-4">You Know the Business. Now Own the Tech Side Too.</h4>
          <p className="text-apple-gray-200 mb-6">
            Let's discuss how to leverage your strengths and fill the technical leadership gap that's holding you back.
          </p>
          <div className="text-center">
            <a
              href="https://form.typeform.com/to/SLoI0dkA"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCTAClick}
              className="inline-block bg-white text-apple-gray-800 font-semibold py-4 px-8 rounded-lg hover:bg-apple-gray-100 transition-colors duration-200 text-lg w-full md:w-auto"
            >
              Take Control Now
            </a>
            <p className="text-apple-gray-300 text-sm mt-3">
              Free 30-min strategy session.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderStrengthsSection;